import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

const PlatformContext = createContext(null);

// Curated font whitelist. Each maps to a Google Fonts family URL.
export const HEADER_FONTS = [
  "Urbanist", "Playfair Display", "Fraunces", "Space Grotesk",
  "Outfit", "Bricolage Grotesque", "DM Serif Display", "Instrument Serif",
];
export const BODY_FONTS = [
  "Google Sans Flex", "DM Sans", "Manrope", "Inter",
  "Plus Jakarta Sans", "Nunito", "Work Sans", "Figtree",
];

function googleFontUrl(name) {
  // Simple encoder — variable-friendly weights for each
  const family = name.replace(/ /g, "+");
  if (name === "Google Sans Flex") return `https://fonts.googleapis.com/css2?family=${family}:opsz,wght@6..144,100..1000&display=swap`;
  if (name === "Fraunces") return `https://fonts.googleapis.com/css2?family=${family}:opsz,wght@9..144,400..800&display=swap`;
  return `https://fonts.googleapis.com/css2?family=${family}:wght@400;500;600;700;800&display=swap`;
}

function ensureFontLoaded(name) {
  if (!name) return;
  const id = `pf-${name.replace(/\s+/g, "-").toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = googleFontUrl(name);
  document.head.appendChild(link);
}

function applyFonts(headerFont, bodyFont) {
  ensureFontLoaded(headerFont);
  ensureFontLoaded(bodyFont);
  const root = document.documentElement;
  root.style.setProperty("--rh-font-header", `'${headerFont}', system-ui, sans-serif`);
  root.style.setProperty("--rh-font-body", `'${bodyFont}', system-ui, sans-serif`);
}

export function PlatformProvider({ children }) {
  const [settings, setSettings] = useState({
    company_name: "OrkaiFlow",
    tagline: "Renovation projects, under one calm roof.",
    logo_url: null,
    header_font: "Urbanist",
    body_font: "Google Sans Flex",
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/public");
      setSettings((s) => ({ ...s, ...data }));
      applyFonts(data.header_font || "Urbanist", data.body_font || "Google Sans Flex");
      // Preserve the "| OS for ID Firms" suffix from index.html — only the
      // company name changes per-tenant, the product tagline stays fixed.
      if (data.company_name) document.title = `${data.company_name} | OS for ID Firms`;
    } catch {
      applyFonts("Urbanist", "Google Sans Flex");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <PlatformContext.Provider value={{ settings, refresh, setSettings }}>
      {children}
    </PlatformContext.Provider>
  );
}

export const usePlatform = () => useContext(PlatformContext);
